import { GetComponentProps } from '@support/types/utility/get-component-props';

import { ComponentProps, FC, PropsWithChildren, ReactNode } from 'react';

import MuiPopover from '@mui/material/Popover';

import { Button, HStack } from '@ui-furniture/elements';
import { StyledProps, x } from '@ui-furniture/style';

import {
  createPositionProps,
  HorizontalPosition,
  VerticalPosition,
} from './create-position-props';
import { usePopoverControls } from './use-popover-controls';

import { IconName, LineIcon } from '../line-icon';

type ButtonProps = {
  iconName?: IconName;
  contentProps?: StyledProps;
  divider?: boolean;
} & ComponentProps<typeof Button>;

const buttonVariant = {
  input: {
    variant: 'input',
    w: '100%',
    size: 'sm',
    iconName: 'edit',
    pl: 'md',
    pr: '0',
    divider: true,
  },
  dropdown: {
    variant: 'input',
    w: '100%',
    size: 'sm',
    iconName: 'chevronDown',
    pl: 'md',
    pr: '0',
    divider: true,
  },
  info: {
    variant: 'input',
    w: '100%',
    size: 'sm',
    iconName: 'questionMarkCircle',
    pl: 'md',
    pr: '0',
  },
  blank: {
    variant: 'blank',
    w: '100%',
    size: 'sm',
    iconName: 'chevronDown',
    p: '0',
    divider: true,
    contentProps: {
      py: '0',
    },
  },
} as const satisfies Record<string, ButtonProps>;

const contentContainerProps = {
  expandable: {
    p: 'md',
  },
  fixedHeight: {
    p: '0',
  },
} as const;

export const Popover: FC<
  PropsWithChildren<
    {
      popoverContent:
        | ReactNode
        | ((actions: { close: () => void }) => ReactNode);
      buttonContent: ReactNode;
      buttonProps?: ButtonProps | keyof typeof buttonVariant;
      contentContainer?: keyof typeof contentContainerProps;
      vPosition?: VerticalPosition;
      hPosition?: HorizontalPosition;
      closeOnSelect?: boolean;
      isDisabled?: boolean;
      onOpenChange?: (isOpen: boolean) => void;
    } & GetComponentProps<typeof x.div>
  >
> = ({
  popoverContent,
  buttonContent,
  buttonProps = {
    icon: null,
  },
  contentContainer = 'expandable',
  vPosition,
  hPosition,
  closeOnSelect = true,
  isDisabled = false,
  onOpenChange,
  ...props
}) => {
  const { iconName, contentProps, divider, ...restButtonProps }: ButtonProps =
    typeof buttonProps === 'string' ? buttonVariant[buttonProps] : buttonProps;

  const { referenceElement, setReferenceElement, isOpen, setIsOpen, actions } =
    usePopoverControls();
  return (
    <x.div
      display='flex'
      pointerEvents={isDisabled ? 'none' : 'auto'}
      minHeight='48px'
      {...props}
    >
      <Button
        type='button'
        variant='blank'
        {...restButtonProps}
        ref={setReferenceElement}
        onClick={() => setIsOpen(true)}
        disabled={isDisabled}
      >
        <HStack alignItems='center' gap='sm' w='100%'>
          <x.span
            flex='1'
            textAlign='left'
            py='md'
            {...contentContainerProps[contentContainer]}
            {...contentProps}
          >
            {buttonContent}
          </x.span>
          {iconName && (
            <x.span display='flex' alignSelf='stretch' alignItems='center'>
              {divider ? (
                <x.span
                  alignSelf='stretch'
                  w='1px'
                  my='md'
                  backgroundColor='palette-border'
                />
              ) : null}
              <x.span
                w='input'
                display='flex'
                justifyContent='center'
                transition='base'
                color={{ hover: 'palette-primary' }}
              >
                <LineIcon name={iconName} size={`24px`} />
              </x.span>
            </x.span>
          )}
        </HStack>
      </Button>
      <MuiPopover
        anchorEl={referenceElement}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onClick={closeOnSelect ? () => setIsOpen(false) : undefined}
        {...createPositionProps({
          vertical: vPosition,
          horizontal: hPosition,
        })}
      >
        <x.div color='palette-text'>
          {typeof popoverContent === 'function'
            ? popoverContent(actions)
            : popoverContent}
        </x.div>
      </MuiPopover>
    </x.div>
  );
};
